/**
 * Which workspace a hostname names.
 *
 * Kept apart from `client.ts` and free of `window` and `import.meta` so it can
 * be exercised directly: the cases that matter are the ones that must *not*
 * resolve, and getting any of those wrong points a browser at another
 * customer's database.
 *
 * The rule is an allowlist, not a denylist. A workspace is the single label in
 * front of a base host the deployment declares — `butchers.diwan.the-alpha.co`
 * against the base `diwan.the-alpha.co`. Everything else names nothing: the
 * base itself is the front door, and a host that is not under the base is not
 * part of this installation at all. A denylist of reserved names cannot say
 * that, and would happily read `marketing.the-alpha.co` as a customer.
 */

/**
 * Labels that are never a workspace even directly under the base host — the
 * usual infrastructure names, in case one is ever pointed at the app.
 */
export const RESERVED_LABELS = new Set([
  'www',
  'app',
  'api',
  'admin',
  'superadmin',
  'internal',
  'static',
  'assets',
  'cdn',
  'mail',
]);

const normalise = (host: string): string => host.trim().toLowerCase().replace(/\.$/, '');

/**
 * `butchers.diwan.the-alpha.co` with base `diwan.the-alpha.co` → `butchers`.
 *
 * Null for the base host itself, for anything outside it, and for every
 * development address — and null is what makes the sign-in form ask for a
 * workspace code instead of guessing one.
 *
 * `baseHosts` is normally a single host, but takes several so one build can
 * serve more than one entry point.
 */
export function tenantFromHost(hostname: string, baseHosts: Iterable<string> = []): string | null {
  const host = normalise(hostname);
  if (!host) return null;

  for (const rawBase of baseHosts) {
    const base = normalise(rawBase);
    if (!base) continue;

    // The base host is the front door, not a customer.
    if (host === base) return null;
    if (!host.endsWith(`.${base}`)) continue;

    const prefix = host.slice(0, -(base.length + 1));
    // Exactly one label. `a.b.diwan.the-alpha.co` is not workspace `a`: a
    // wildcard certificate covers one level, so a deeper name is a mistake
    // rather than a customer.
    if (prefix.includes('.')) return null;
    if (RESERVED_LABELS.has(prefix)) return null;
    // Still has to look like a workspace slug, or a malformed host becomes a
    // code the API then rejects for a much less obvious reason.
    if (!/^[a-z0-9][a-z0-9-]*$/.test(prefix)) return null;
    return prefix;
  }

  // No base declared, or the address is not under one. Localhost, an IP and any
  // unrelated subdomain all land here.
  return null;
}
